import {cloneDeep} from 'lodash';
import {combineReducers} from '@reduxjs/toolkit';

import createStore from 'common/stores/app-store';
import {
  ADD_FORMSET_CHILD_FORM,
  CLEAR_FORM,
  FETCH_FORM_DATA_FAILURE,
  FETCH_FORM_DATA_START,
  FETCH_FORM_DATA_SUCCESS,
  SET_FORM_DATA,
  SET_FORM_STEP,
  SUBMIT_FORM_START,
  SUBMIT_FORM_FAILURE,
  SUBMIT_FORM_SUCCESS,
  UPDATE_FORM_SET,
  UPDATE_FORM_VALUE,
} from 'common/components/form/actions';
import {getChildFormByPrefix} from 'common/components/form/utils';

export const initialState = {
  isLoading: true,
  isSaving: false,
  // represents an error not covered by form validation -- network errors, 500s, etc.
  // form validation errors are stored in the form object itself
  error: null,
  form: null,
};

const formReducer = (state = initialState, {type, payload}) => {
  switch (type) {
    case SET_FORM_DATA: {
      return {
        form: payload.form,
        isLoading: false,
        isSaving: false,
        error: null,
      };
    }
    case FETCH_FORM_DATA_START: {
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    }
    case FETCH_FORM_DATA_FAILURE:
    case SUBMIT_FORM_FAILURE: {
      return {
        ...state,
        isLoading: false,
        isSaving: false,
        error: payload.error,
      };
    }
    case FETCH_FORM_DATA_SUCCESS: {
      return {
        ...state,
        form: payload.form,
        isLoading: false,
        error: null,
      };
    }
    case SUBMIT_FORM_START: {
      return {
        ...state,
        isSaving: true,
        error: null,
      };
    }
    case SUBMIT_FORM_SUCCESS: {
      return {
        ...state,
        isSaving: false,
        error: null,
        form: payload.form,
      };
    }
    case UPDATE_FORM_VALUE: {
      const form = cloneDeep(state.form);
      if (payload.prefix) {
        getChildFormByPrefix(form, payload.prefix).data[payload.name] =
          payload.value;
      } else {
        form.data[payload.name] = payload.value;
      }
      return {
        ...state,
        form,
      };
    }
    case UPDATE_FORM_SET: {
      // When a formset child form related field changes, the backend
      // only returns an updated version of that child form (the rest
      // of the child forms are empty since they are not regenerated).
      // So, this updates the stored form instead of setting it.
      const form = cloneDeep(state.form);
      const childFormIdx = parseInt(payload.childForm.prefix.split('-')[1]);
      form.formset.forms[childFormIdx] = payload.childForm;
      form.formset.meta = payload.meta;
      return {
        ...state,
        form,
      };
    }
    case ADD_FORMSET_CHILD_FORM: {
      const {TOTAL_FORMS, MAX_NUM_FORMS} = form.formset.meta;
      if (TOTAL_FORMS >= MAX_NUM_FORMS) {
        return form;
      }

      const form = cloneDeep(state.form);

      const lastChildForm = form.formset.forms.slice(-1)[0];
      const [_prefix, _idx] = lastChildForm.prefix.split('-');
      const lastChildFormIdx = parseInt(_idx);

      const newChildForm = cloneDeep(lastChildForm);
      const newChildFormIdx = lastChildFormIdx + 1;
      newChildForm.prefix = `${_prefix}-${newChildFormIdx}`;
      form.formset.forms.push(newChildForm);

      form.formset.meta.TOTAL_FORMS += 1;

      return {
        ...state,
        form,
      };
    }
    case SET_FORM_STEP: {
      const form = cloneDeep(state.form);
      form.step = payload.step;
      return {
        ...state,
        form,
      };
    }
    case CLEAR_FORM: {
      return initialState;
    }
    default: {
      return state;
    }
  }
};

export const setupFormStore = (formId) =>
  createStore(
    combineReducers({
      [formId]: formReducer,
    }),
    {
      [formId]: initialState,
    },
    {
      logger: false,
    },
  );

export default formReducer;
